import React, { useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Paper, Snackbar, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import ReportTable from '../components/ReportTable';
import DocumentReportFilterPanel from '../components/filters/DocumentReportFilterPanel';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetDocumentReportsHandler } from '../dataHandlers/getReportsHandler';
import { useCreateReportsJobHandler } from '../dataHandlers/createReportJobHandler';
import { reportTypes } from '../../../constants/appConstants';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import useSubscriptionCheck from '../../common/SubscriptionCheck';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';
import { CREATE_DOCUMENT_REPORT } from '../../../constants/eInvoicePermissions';
import { getDocumentReportGenerateAccess } from '../utils/getDocumentReportGenerateAccess';
import useDocumentReportFilterOptions from '../hooks/useDocumentReportFilterOptions';
import useDocumentReportFilters from '../hooks/useDocumentReportFilters';

const DocumentReportPage = () => {
  const { documentReports: dr } = useTranslatedMessages();
  const docReport = dr.document_report;
  useDocumentTitle(docReport.page_title);
  const { org } = useSelector((/** @type {any} */ state) => state.org);
  const user = useSelector((/** @type {any} */ state) => state.user.user);
  const isOrganizationOwner = Array.isArray(user?.roles) && user.roles.includes('ORGANIZATION_OWNER');
  const { checkSubscription, SubscriptionModal } = useSubscriptionCheck();
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const canCreate = hasEInvoicePermission(CREATE_DOCUMENT_REPORT);
  const rootUserEmail = org?.root_user_email ?? '';
  const { hasSalesPurchaseScope, tooltipMessage } = getDocumentReportGenerateAccess({
    hasEInvoicePermission,
    docReportLabels: docReport,
    adminEmail: rootUserEmail,
  });
  const generateTooltip = !canCreate ? accessDeniedMessage : tooltipMessage;
  const userEmail = org?.user?.email || '';
  const [state, setState] = useState({
    page: 0,
    size: 10,
    localSuccessSnackbarOpen: false,
  });

  const filterOptions = useDocumentReportFilterOptions();
  const {
    filters,
    appliedFilters,
    canGenerate,
    dayCount,
    touched,
    validationErrors,
    markTouched,
    updateFilter,
    removeAppliedFilter,
    clearAllFilters,
    buildReportPayload,
  } = useDocumentReportFilters({ filterOptions });

  const { page, size, localSuccessSnackbarOpen } = state;

  const { reports, totalReports, loading } = useGetDocumentReportsHandler({
    searchQuery: { page, size, type: reportTypes.DOCUMENT_REPORT },
  });

  const { onSubmit, isPending: generating } = useCreateReportsJobHandler({
    suppressSuccessSnackbar: true,
    onSuccess: () => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: true })),
  });

  const handleGenerateReport = () => {
    markTouched();

    if (!hasEInvoicePermission(CREATE_DOCUMENT_REPORT)) {
      showActionDeniedSnackbar();
      return;
    }
    if (!hasSalesPurchaseScope || !canGenerate) {
      return;
    }
    if (!checkSubscription()) {
      return;
    }

    onSubmit(buildReportPayload(reportTypes.DOCUMENT_REPORT));
  };

  return (
    <Stack direction="column" spacing={3} sx={{ mt: '-1rem' }}>
      <Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {docReport.page_title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {docReport.page_subtitle}
        </Typography>
      </Box>

      <DocumentReportFilterPanel
        filters={filters}
        appliedFilters={appliedFilters}
        filterOptions={filterOptions}
        validationErrors={validationErrors}
        touched={touched}
        dayCount={dayCount}
        onFilterChange={updateFilter}
        onRemoveAppliedFilter={removeAppliedFilter}
        onClearAllFilters={clearAllFilters}
        onGenerate={handleGenerateReport}
        generateDisabled={!hasSalesPurchaseScope || !canCreate || !canGenerate || generating}
        generateLoading={generating}
        generateTooltip={generateTooltip}
      />

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          '& .MuiAlert-icon': { color: 'text.secondary' },
        }}
      >
        {docReport.async_generation_note}
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DescriptionOutlinedIcon fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {docReport.generated_reports}
          </Typography>
        </Stack>

        <ReportTable
          reports={reports}
          loading={loading}
          totalReports={totalReports}
          showCreatedBy={isOrganizationOwner}
          useAuthenticatedDownload
          downloadDefaultFileName="document_report.xlsx"
          downloadInsufficientAccessMessage={docReport.download_insufficient_access}
          onPageChange={(newPage) => setState((prev) => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newSize) => setState((prev) => ({ ...prev, size: newSize }))}
        />
      </Paper>

      <SubscriptionModal />

      <Snackbar
        open={localSuccessSnackbarOpen}
        onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        >
          {docReport.report_initiated_success}{' '}
          <a href={`mailto:${userEmail}`} style={{ fontWeight: 'bold', color: 'inherit', textDecoration: 'underline' }}>
            {userEmail}
          </a>{' '}
          {docReport.report_initiated_success_suffix}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default DocumentReportPage;
